import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Send } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function IncidenciaAlumnoDetalle() {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canCreate = user?.role === "profesor";

    const [incidents, setIncidents] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [courseId, setCourseId] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const calls = [api.get(`/incidents/student/${studentId}`)];
            if (canCreate) calls.push(api.get("/courses"));
            const results = await Promise.all(calls);
            setIncidents(results[0].data || []);
            if (canCreate) setCourses(results[1].data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando incidencias");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [studentId]);

    async function submit() {
        if (!courseId) {
            toast.error("Elige en qué curso ocurrió");
            return;
        }
        if (!descripcion.trim()) {
            toast.error("Escribe qué pasó");
            return;
        }
        setSaving(true);
        try {
            await api.post("/incidents", { student_id: studentId, course_id: courseId, descripcion: descripcion.trim() });
            toast.success("Incidencia registrada");
            setDescripcion("");
            setCourseId("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error registrando la incidencia");
        } finally {
            setSaving(false);
        }
    }

    const studentName = incidents[0]?.student_name;

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <h1 className="text-xl font-semibold mb-1 inline-flex items-center gap-2"><AlertTriangle size={20} className="text-edu-navy" /> {studentName || "Incidencias"}</h1>
            <p className="text-edu-muted mb-6">{incidents.length} incidencia{incidents.length !== 1 ? "s" : ""} registrada{incidents.length !== 1 ? "s" : ""}.</p>

            {canCreate && (
                <div className="edu-card p-6 mb-6 space-y-3">
                    <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest">Nueva incidencia</h2>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Curso en el que ocurrió</span>
                        <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className="edu-input w-full">
                            <option value="">Selecciona un curso…</option>
                            {courses.map((c) => <option key={c.id} value={c.id}>{c.name} — {(c.sections || []).join(", ")}</option>)}
                        </select>
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Qué pasó</span>
                        <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={4} className="edu-input w-full" placeholder="Describe la incidencia…" />
                    </div>
                    <div className="flex justify-end">
                        <button onClick={submit} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                            <Send size={14} /> {saving ? "Guardando…" : "Registrar"}
                        </button>
                    </div>
                </div>
            )}

            <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest mb-3">Historial</h2>
            {loading ? (
                <p className="text-sm text-edu-muted">Cargando…</p>
            ) : incidents.length === 0 ? (
                <div className="edu-card p-8 text-center text-edu-muted">Sin incidencias registradas.</div>
            ) : (
                <div className="space-y-3">
                    {incidents.map((i) => (
                        <div key={i.id} className="edu-card p-4">
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-medium">{i.course_name}</p>
                                <p className="text-xs text-edu-muted">{new Date(i.created_at).toLocaleString("es-PE")}</p>
                            </div>
                            <p className="text-sm text-edu-body">{i.descripcion}</p>
                            <p className="text-xs text-edu-muted mt-1">Registrado por {i.created_by_name}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
